(ns story-builder.story-editor.rich-text.content
  (:require
   [clojure.data :as data]
   [clojure.spec.alpha :as s]
   [clojure.string :as str]
   [clojure.walk :as walk]
   [medley.core :as m]
   [story-builder.font.constant :as font.constant]
   [story-builder.util.core :as util]
   [story-builder.util.css :as util.css]))

(s/def ::text string?)
(s/def ::tag string?)
(s/def ::font-id string?)
(s/def ::children (s/+ ::text-node))
(s/def ::style map?)
(s/def ::text-node (s/and
                    (s/keys :req-un [::text]
                            :opt-un [::font-id
                                     ::style
                                     ::tag])))
(s/def ::block-node (s/and
                     (s/keys :req-un [::children
                                      ::tag]
                             :opt-un [::style])))
(s/def ::content (s/+ ::block-node))

(defn blank?
  "Returns true if there is no text inside the content.
   Equivalent to str/blank? if the content was a string."
  [content]
  (and (= (count content) 1) ; One block node
       (= (count (:children (first content))) 1) ; That block has one text node
       (-> content ; And the text node is empty
           first
           :children
           first
           :text
           (str/blank?))))

(defn content-text
  "Returns a string of the text from rich-text content"
  [content]
  (->> (for [block content]
         (->> block
              (tree-seq map? :children)
              (keep :text)
              (str/join)))
       ;; Separate blocks with a space
       (str/join " ")))

(defn content-stats
  "Returns a map with letter count, word count, and sentence count for rich-text content.
   - letters: count of all characters (excluding whitespace)
   - words: count of words (sequences of word characters)
   - sentences: count of sentences (text ending with . ! ? followed by whitespace or end of string)"
  [content]
  (let [text      (content-text content)
        letters   (count (str/replace text #"\s" ""))
        words     (count (re-seq #"\w+" text))
        sentences (count (re-seq #"[.!?]+(?:\s|$)" text))]
    {:letters   letters
     :words     words
     :sentences sentences}))

(comment
  (content-text [{:children
                  [{:font-id "1c80fbdb-b2f4-11eb-ac5c-bff969b462d2"
                    :style        {:font-family "Abril Normal",
                                   :font-size   "1.6875em"},
                    :text         "Here"}
                   {:style {:font-family "Abril Normal"
                            :font-size   "1.6875em"
                            :opacity     "1"},
                    :text  " is one block."}],
                  :style {:text-align "right"},
                  :tag "h2"}
                 {:children [{:font-id "363bf38a-b30c-11eb-aace-f45e8e84f6b8"
                              :style        {:font-family "Acumin Pro Condensed Normal",
                                             :font-size   "1.125em"},
                              :text         "Here is another block."}],
                  :style {:text-align "right"},
                  :tag "p"}])
  ;; => "Here is one block. Here is another block."

  (content-stats [{:children
                   [{:text "Hello world! This is a test."}
                    {:text "Another sentence here."}],
                   :tag "p"}])
  ;; => {:letters 45, :words 9, :sentences 2}
  )

(defn resolve-data-bindings
  [content resolver]
  (walk/prewalk (fn [x]
                  (cond-> x
                    (or (and (= (:type x) "data-binding") (:data x))
                        (:schema-binding x))
                    (resolver x)))
                content))

(defn update-text
  "Updates all text with a function f"
  [content f]
  (walk/prewalk (fn [x]
                  (cond-> x
                    (and (:text x) (string? (:text x)))
                    (update :text f)))
                content))

(defn update-font-families
  "Updates font styles based on :font-id.
   - If :font-id matches a font family ID or media ID, set :style/:font-family to the family name.
   - If :font-id matches a variant's media ID, also set :style/:font-weight to that variant's weight."
  [content fonts-by-id]
  (walk/prewalk (fn [x]
                  (if-let [font-id (:font-id x)]
                    (if-let [family (get fonts-by-id font-id)]
                      (let [variant (some (fn [v]
                                            (when (some #(= (:id %) font-id) (:media v))
                                              v))
                                          (:variants family))
                            x       (cond-> (assoc-in x [:style :font-family] (util/single-quoted (:name family)))
                                      (:weight variant)
                                      (assoc-in [:style :font-weight]
                                                (get font.constant/weight-choice->weight (:weight variant)))
                                      (:style variant)
                                      (assoc-in [:style :font-style] (str/lower-case (:style variant))))]
                        x)
                      x)
                    x))
                content))

(comment
  (let [layer {:content [{:children [{:font-id "1c80fbdb-b2f4-11eb-ac5c-bff969b462d2"
                                      :style   {:font-family "Abril Normal",
                                                :font-size   "1.6875em"},
                                      :text    "Here"}
                                     {:style {:font-family "Abril Normal"
                                              :font-size   "1.6875em"
                                              :opacity     "1"},
                                      :text  " is one block."}],
                          :style    {:text-align "right"},
                          :tag      "h2"}
                         {:children [{:font-id "363bf38a-b30c-11eb-aace-f45e8e84f6b8"
                                      :style   {:font-family "Acumin Pro Condensed Normal",
                                                :font-size   "1.125em"},
                                      :text    "Here is another block."}],
                          :style    {:text-align "right"},
                          :tag      "p"}]}
        fonts [{:public-id "1c80fbdb-b2f4-11eb-ac5c-bff969b462d2"
                :file-name "Abril Normal [NEW-FONT-FAMILY]"}
               {:public-id "363bf38a-b30c-11eb-aace-f45e8e84f6b8"
                :file-name "Acumin Pro Condensed Normal [NEW-FONT-FAMILY]"}]
        fonts-by-id (reduce
                     (fn [db f]
                       (assoc db (:public-id f) f))
                     {} fonts)]
    (update-font-families layer fonts-by-id))
  ;; => {:content
  ;;       [{:children [{:font-id "1c80fbdb-b2f4-11eb-ac5c-bff969b462d2",
  ;;                     :style {:font-family "Abril Normal [NEW-FONT-FAMILY]",
  ;;                             :font-size "1.6875em"},
  ;;                     :text "Here"}
  ;;                    {:style {:font-family "Abril Normal",
  ;;                             :font-size "1.6875em",
  ;;                             :opacity "1"},
  ;;                     :text " is one block."}],
  ;;         :style {:text-align "right"},
  ;;         :tag "h2"}
  ;;        {:children [{:font-id "363bf38a-b30c-11eb-aace-f45e8e84f6b8",
  ;;                     :style {:font-family
  ;;                               "Acumin Pro Condensed Normal [NEW-FONT-FAMILY]",
  ;;                             :font-size "1.125em"},
  ;;                     :text "Here is another block."}],
  ;;         :style {:text-align "right"},
  ;;         :tag "p"}]}
  )

(defn all-attributes-equal?
  "Returns true if the style of all the text nodes in the content are equal."
  [content]
  (let [all-nodes               (mapcat (fn [block]
                                          (->> block
                                               (tree-seq map? :children)))
                                        content)
        {text-nodes  true
         block-nodes false}     (group-by #(some? (:text %)) all-nodes)
        nodes-attributes-equal? (fn [nodes]
                                  (let [attribute-only-nodes (map #(dissoc % :text :children :tag)
                                                                  nodes)]
                                    (apply = attribute-only-nodes)))]
    (and (nodes-attributes-equal? text-nodes)
         (nodes-attributes-equal? block-nodes))))

(comment
  (all-attributes-equal? [{:children [{:text "1st some text"}],
                           :tag "div"}
                          {:children [{:text "2nd some text"}],
                           :style {:text-align "right"},
                           :tag "div"}])
  ;; => false

  (all-attributes-equal? [{:children [{:text "1st some text"
                                       :style {:font-size "3em"}}],
                           :tag "div"}
                          {:children [{:text "2nd some text"
                                       :style {:font-size "3em"}}],
                           :tag "div"}])
  ;; => true
  )

(defn first-node-attributes [content]
  (let [first-block-node (first content)
        first-text-node  (first (:children first-block-node))]
    {:text-node-attributes  (dissoc first-text-node :text)
     :block-node-attributes (dissoc first-block-node :children)}))

(comment
  (first-node-attributes [{:children [{:text "1st some text"
                                       :style {:font-size "3em"}}],
                           :tag "div"}
                          {:children [{:text "2nd some text"
                                       :style {:font-size "3em"}}],
                           :tag "div"}]))

(defn between-paths?
  "Returns true if this path is between start-path and end-path."
  [path start-path end-path]
  (let [between? (fn [x start stop]
                   (and (<= start x)
                        (<= x stop)))]
    (every? true? (map between? path start-path end-path))))

(comment
  (between-paths? [] [] []) ;; => true
  (between-paths? [1] [0] [2]) ;; => true
  (between-paths? [1] [2] [3]) ;; => false
  (between-paths? [0 1 3] [0 1 4] [0 1]) ;; => true
  (between-paths? [0 1 3] [0 2] [0 1 4]) ;; => false
  )

(defn leaf-nodes-between
  "Returns a list of leaf nodes between start-path and end-path, where the paths are from the root
   Node ::= TextNode | Vector[Node] | BlockNode"
  [node start-path end-path]
  (let [loop-fn (fn loop-fn [node current-path]
                  (if (between-paths? current-path start-path end-path)
                    (cond
                      (some? (:text node))
                      [node]

                      (vector? node)
                      (apply concat (for [[idx child] (util/seq-indexed node)]
                                      (let [child-path (conj current-path idx)]
                                        (loop-fn child child-path))))

                      (some? (:children node))
                      (loop-fn (:children node) current-path))
                    nil))]
    (loop-fn node [])))

(defn block-nodes-between
  "Returns a list of block nodes between start-path and end-path, where the paths are from the root
   Node ::= TextNode | Vector[Node] | BlockNode"
  [root start-path end-path]
  (let [loop-fn (fn loop-fn [node current-path]
                  (if (between-paths? current-path start-path end-path)
                    (cond
                      (vector? node)
                      (apply concat (for [[idx child] (util/seq-indexed node)]
                                      (let [child-path (conj current-path idx)]
                                        (loop-fn child child-path))))

                      (some? (:children node))
                      (conj (loop-fn (:children node) current-path) (dissoc node :children))

                      :else
                      [])
                    nil))]
    (loop-fn root [])))

(defn things-in-both
  "Recursively diffs a and b to find the common values. Maps are subdiffed where keys match and values differ."
  [a b]
  (nth (data/diff a b) 2))

(defn universal-leaf-props
  "Returns a map of common props for all leaf nodes between start-path and end-path"
  [node start-path end-path]
  (let [leaf-nodes (->> (leaf-nodes-between node start-path end-path)
                        (map #(dissoc % :text)))]
    (reduce things-in-both leaf-nodes)))

(defn universal-block-props
  "Returns a map of common props for all block nodes between start-path and end-path"
  [node start-path end-path]
  (let [block-nodes (->> (block-nodes-between node start-path end-path)
                         (map #(dissoc % :children)))]
    (reduce things-in-both block-nodes)))

(comment
  ;;;;;;;;;; Querying the node tree
  (def root [{:tag "p"
              :children [{:tag "p"
                          :children [{:text ""}
                                     {:text ""}
                                     {:tag "h1"
                                      :children [{:text ""}]}]}]}
             {:tag "h2"
              :children [{:tag "p"
                          :children [{:text "hey"}]}]}])
  (def start-path [0])
  (def stop-path [1])
  (block-nodes-between root start-path stop-path)
  ;; => ({:tag "p"} {:tag "p"} {:tag "p"} {:tag "p"})

  (def root [{:tag "p"
              :children [{:tag "p"
                          :children [{:text "some"}
                                     {:text "text"}
                                     {:text "here"}]}]}
             {:tag "p"
              :children [{:tag "p"
                          :children [{:text "hey"}]}]}])
  (universal-block-props root start-path stop-path)
  ;; => {:tag "p"}
  )

(defn paths-to-leaves
  "Returns a sequence of all key paths to leaves in a given map using DFS walk."
  [m]
  (mapcat (fn [[k v]]
            (if (map? v)
              (->> (paths-to-leaves v)
                   (map #(vec (concat [k] %))))
              [[k]]))
          m))

(comment
  (paths-to-leaves {:children [{:font-id "48390594-b30f-11eb-bf04-6cfd9ad2983a",
                                :text "Here's another piece of text"}],
                    :style {:font-family "SF Pro Text",
                            :font-size "1em",
                            :font-weight "bold",
                            :line-height "1.25em"},
                    :tag "div"})
  ;; => ([:children]
  ;;     [:style :font-family]
  ;;     [:style :font-size]
  ;;     [:style :font-weight]
  ;;     [:style :line-height]
  ;;     [:tag])
  )

(defn optimize [block-nodes]
  (vec (for [block-node block-nodes]
         (let [children-universal-props (->> (:children block-node)
                                             (map #(select-keys % [:style :font-id]))
                                             (reduce things-in-both))
               children-universal-props (-> children-universal-props
                                            ;; line-height depends on font-size, so don't pull up line-height if there are different font-sizes
                                            (cond-> (and (some? (get-in children-universal-props [:style :line-height]))
                                                         (nil? (get-in children-universal-props [:style :font-size])))
                                              (m/dissoc-in [:style :line-height]))
                                            (m/dissoc-in [:style :vertical-align]) ; vertical-align shouldn't be lifted because it's not inherited by default.
                                            (m/dissoc-in [:style :border-radius]) ; border-radius behaves differently on a span vs the wrapping div, so shouldn't be lifted
                                            (m/dissoc-in [:style :border]) ; border behaves differently on a span vs the wrapping div, so shouldn't be lifted
                                            (m/dissoc-in [:style :background]) ; background behaves differently on a span vs the wrapping div, so shouldn't be lifted
                                            (m/dissoc-in [:style :padding]) ; padding behaves differently on a span vs the wrapping div, so shouldn't be lifted
                                            (m/dissoc-in [:style :background-color])) ; the background depends on background color and width, height, and padding, so shouldn't be lifted up
               children-universal-paths (paths-to-leaves children-universal-props)]
           (-> block-node
               (update :children
                       (fn [children]
                         (vec (for [child children]
                                (reduce m/dissoc-in
                                        child
                                        children-universal-paths)))))
               (->> (util/deep-merge children-universal-props)))))))

(comment
  (def content [{:children [{:font-id "48390594-b30f-11eb-bf04-6cfd9ad2983a",
                             :style {:font-family "SF Pro Text",
                                     :font-size "1.7em",
                                     :font-weight "bold",
                                     :line-height "1.25em"},
                             :text "Some"}
                            {:font-id "48390594-b30f-11eb-bf04-6cfd9ad2983a",
                             :style {:font-family "SF Pro Text",
                                     :font-size "2em",
                                     :font-weight "bold",
                                     :line-height "1.25em",
                                     :opacity 0.713},
                             :text " text"}],
                 :tag "div"}
                {:children [{:font-id "48390594-b30f-11eb-bf04-6cfd9ad2983a",
                             :style {:font-family "SF Pro Text",
                                     :font-size "1em",
                                     :font-weight "bold",
                                     :line-height "1.25em"},
                             :text "Here's another piece of text"}],
                 :tag "div"}])

  (optimize content)
  ;; => [{:children [{:font-id "48390594-b30f-11eb-bf04-6cfd9ad2983a",
  ;;                  :style {:font-size "1.7em", :line-height "1.25em"},
  ;;                  :text "Some"}
  ;;                 {:font-id "48390594-b30f-11eb-bf04-6cfd9ad2983a",
  ;;                  :style {:font-size "2em", :line-height "1.25em", :opacity 0.713},
  ;;                  :text " text"}],
  ;;      :style {:font-family "SF Pro Text", :font-weight "bold"},
  ;;      :tag "div"}
  ;;     {:children [{:font-id "48390594-b30f-11eb-bf04-6cfd9ad2983a",
  ;;                  :style {:line-height "1.25em"},
  ;;                  :text "Here's another piece of text"}],
  ;;      :style {:font-family "SF Pro Text", :font-size "1em", :font-weight "bold"},
  ;;      :tag "div"}]
  )

(defn optimized-content-div [content]
  (first (optimize [{:children (optimize content)
                     :tag      "div"}])))

(comment
  (let [content [{:children [{:style {:text "Some text"}}
                             {:style {:vertical-align "super"},
                              :text "abc"}],
                  :tag "div"}]]
    (optimized-content-div content))
  ;; => {:children [{:children [{:text "Some text"}
  ;;                            {:style {:vertical-align "super"}, :text "abc"}],
  ;;                 :tag "div"}],
  ;;     :tag "div"}
  )

(defn elementify-styles
  "Converts certain styles to wrapping nodes.
   Used to convert vertical-align styles to <sub> or <sup> nodes.
   Could also be used to convert bold/italic/underline styles to <b>/<i>/<u> nodes."
  [content]
  (walk/prewalk (fn [node]
                  (if (and (get-in node [:style :vertical-align])
                           (:text node))
                    (let [vertical-align-tag (case (get-in node [:style :vertical-align])
                                               "sub"   "sub"
                                               ;; FIXME There shouldn't be both sup and super here, but we seem to have a mix in the wild.
                                               "super" "sup"
                                               "sup"   "sup")
                          node-attrs         (-> node
                                                 (dissoc :text)
                                                 (m/dissoc-in [:style :vertical-align]))]
                      (if (empty? node-attrs)
                        {:tag  vertical-align-tag
                         :text (:text node)}
                        (merge node-attrs
                               {:tag      (get node :tag "span")
                                :children [{:tag  vertical-align-tag
                                            :text (:text node)}]})))
                    node))
                content))

(comment
  (let [content [{:children [{:style {:text "Some text"}}
                             {:style {:vertical-align "super"},
                              :text "abc"}],
                  :tag "div"}]]
    (elementify-styles (optimized-content-div content)))
  ;; => {:children [{:children [{:text "Some text"}
  ;;                            {:tag "sup", :text "abc"}],
  ;;                 :tag "div"}],
  ;;     :tag "div"}
  )

(defn divide-font-size [content font-size-divisor]
  (walk/prewalk (fn [node]
                  (cond-> node
                    (:font-size node)
                    (update :font-size (fn [font-size-str]
                                         (let [font-size-num (-> font-size-str
                                                                 (str/replace "em" "")
                                                                 js/parseFloat)]
                                           (str "calc(" font-size-num "em / " font-size-divisor ")"))))))
                content))

(defn- as-hiccup-node
  [tag attributes children]
  (if (empty? attributes)
    (into [(keyword tag)] children)
    (into [(keyword tag) (update attributes :style util.css/with-vendor-prefixes)] children)))

(defn as-hiccup
  "Converts a content node tree into hiccup.
   Node ::= Vector[Node] | TextNode | BlockNode"
  [node]
  (cond
    (vector? node)
    (->> node
         (map as-hiccup)
         (concat)
         (vec))

    (:text node) ; inline text node
    (let [attributes (when (:style node)
                       {:style (:style node)})
          text       (if (= (:text node) "")
                       [:br]
                       (:text node))
          tag        (get node :tag "span")]
      (if (and (empty? attributes)
               (= tag "span"))
        text
        (as-hiccup-node tag attributes [text])))

    (:children node) ; block node
    (let [attributes      (when (:style node)
                            {:style (:style node)})
          hiccup-children (as-hiccup (:children node))]
      (as-hiccup-node (get node :tag "div") attributes hiccup-children))))
